import React, { Fragment, useState, useEffect, useCallback } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import { Link, useSearchParams } from "react-router-dom";
import { Row, Col, Card, Table, Badge, Dropdown, Button, Spinner, Form } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import PortalMenu from "../Common/PortalMenu";
import SignatureModal from "../Common/SignatureModal";
import api from "../../../api";
import swal from "sweetalert";

const ContractorDeliveryList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);

  const [currentPage, setCurrentPage] = usePersistentState("contractor_delivery_page", 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [previewingPdf, setPreviewingPdf] = useState(null);

  const [search, setSearch] = usePersistentState("contractor_delivery_search", "");
  const [selectedStatus, setSelectedStatus] = usePersistentState("contractor_delivery_status", "");
  const [approvingId, setApprovingId] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const selectedProject = searchParams.get("project") || "";
  const selectedWorkOrder = searchParams.get("workOrder") || "";

  // 1. Load Projects & Work Orders for filters
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [projRes, woRes] = await Promise.all([
          api.get("/projects?limit=all"),
          api.get("/work-orders?limit=all"),
        ]);
        setProjects(projRes.data?.data || []);
        setWorkOrders(woRes.data?.data || []);
      } catch (err) {
        console.error("Filter load error", err);
      }
    };
    fetchFilters();
  }, []);

  // 2. Fetch Deliveries
  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", 15);
      if (selectedProject) params.append("project", selectedProject);
      if (selectedWorkOrder) params.append("workOrder", selectedWorkOrder);
      if (selectedStatus) params.append("status", selectedStatus);
      if (search.trim()) params.append("search", search.trim());

      const res = await api.get(`/contractor-delivery?${params.toString()}`);
      setDeliveries(res.data?.data || []);
      setTotalPages(res.data?.pagination?.totalPages || 1);
      setTotalCount(res.data?.pagination?.totalCount || 0);
    } catch (err) {
      console.error("fetchDeliveries error", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedProject, selectedWorkOrder, selectedStatus, search]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  // Handle PDF Preview
  const handlePreviewPdf = async (id) => {
    try {
      setPreviewingPdf(id);
      const res = await api.get(`/contractor-delivery/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      window.setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      swal("Error", "Failed to preview PDF", "error");
    } finally {
      setPreviewingPdf(null);
    }
  };

  // 3. Handle PDF Download
  const handleDownloadPdf = async (id, deliveryNumber) => {
    try {
      swal({
        title: "Generating PDF",
        text: "Please wait while your PDF is being generated...",
        icon: "info",
        buttons: false,
        closeOnClickOutside: false,
      });

      const res = await api.get(`/contractor-delivery/${id}/pdf`, { responseType: "blob" });
      swal.close();

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Contractor_DC_${(deliveryNumber || "CDC").replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      swal.close();
      swal("Error", "Failed to generate PDF", "error");
    }
  };

  // 4. Handle Delete
  const handleDelete = (id, number) => {
    swal({
      title: "Are you sure?",
      text: `Do you want to delete Contractor Delivery Challan ${number || ""}?`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Delete"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await api.delete(`/contractor-delivery/${id}`);
          swal("Deleted", "Contractor Delivery Challan deleted successfully", "success");
          fetchDeliveries();
        } catch (err) {
          swal("Error", err.response?.data?.message || "Failed to delete", "error");
        }
      }
    });
  };

  const handleAccept = (id) => {
    setApprovingId(id);
    setShowApproveModal(true);
  };

  const handleSaveSignature = async (file, stampFile, remarks) => {
    if (!approvingId) return;
    try {
      setActionLoading(approvingId);
      const formData = new FormData();
      if (file) {
        formData.append("signature", file);
      }
      if (remarks) {
        formData.append("remarks", remarks);
      }

      const res = await api.put(`/contractor-delivery/${approvingId}/approve`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        setDeliveries((prev) =>
          prev.map((d) => (d._id === approvingId ? { ...d, status: "Accepted", approvalRemarks: remarks } : d))
        );
        swal("Accepted", "Contractor Delivery Challan accepted successfully", "success");
        setShowApproveModal(false);
        setApprovingId(null);
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to accept Contractor Delivery", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    const value = await swal({
      title: "Reject Contractor Delivery?",
      text: "Please provide a reason / remarks for rejection:",
      content: "input",
      icon: "warning",
      buttons: ["Cancel", "Reject"],
      dangerMode: true,
    });

    if (value === null) return; // user cancelled

    try {
      setActionLoading(id);
      const res = await api.put(`/contractor-delivery/${id}/reject`, {
        remarks: value || "",
      });

      if (res.data.success) {
        setDeliveries((prev) =>
          prev.map((d) => (d._id === id ? { ...d, status: "Rejected", approvalRemarks: value } : d))
        );
        swal("Rejected", "Contractor Delivery Challan rejected successfully", "success");
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to reject Contractor Delivery", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Draft":
        return <Badge bg="secondary">Draft</Badge>;
      case "Accepted":
        return <Badge bg="success">Accepted</Badge>;
      case "Rejected":
        return <Badge bg="danger">Rejected</Badge>;
      case "Pending":
      default:
        return <Badge bg="warning" text="dark">Pending</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu="Contractor Deliveries" motherMenu="Material Management" />

      <div className="row">
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <Card.Title className="mb-0">
                  <i className="fas fa-truck-moving text-primary me-2"></i>
                  Contractor Delivery Challans (DC)
                </Card.Title>
                <small className="text-muted">Manage all contractor-bound delivery and dispatch challans</small>
              </div>

              <div className="d-flex gap-2">
                <Link
                  to={`/contractor-delivery/add${selectedProject ? `?project=${selectedProject}` : ""}${selectedWorkOrder ? `&workOrder=${selectedWorkOrder}` : ""}`}
                  className="btn btn-primary btn-sm rounded-pill"
                >
                  <i className="fas fa-plus me-1"></i> Create Contractor DC
                </Link>
              </div>
            </Card.Header>

            <Card.Body>
              {/* FILTERS */}
              <Row className="mb-3 g-2">
                <Col md={3}>
                  <Form.Control
                    type="text"
                    placeholder="Search by DC No, Challan, Vehicle, Driver..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Col>

                <Col md={3}>
                  <Form.Select
                    value={selectedProject}
                    onChange={(e) => {
                      const p = e.target.value;
                      setSearchParams((prev) => {
                        if (p) prev.set("project", p);
                        else prev.delete("project");
                        prev.delete("workOrder");
                        return prev;
                      });
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Projects</option>
                    {projects.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.projectName}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <Form.Select
                    value={selectedStatus}
                    onChange={(e) => {
                      setSelectedStatus(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Statuses</option>
                    <option value="Pending">Pending</option>
                    <option value="Accepted">Accepted</option>
                    <option value="Rejected">Rejected</option>
                    <option value="Draft">Draft</option>
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <Form.Select
                    value={selectedWorkOrder}
                    onChange={(e) => {
                      const wo = e.target.value;
                      setSearchParams((prev) => {
                        if (wo) prev.set("workOrder", wo);
                        else prev.delete("workOrder");
                        return prev;
                      });
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Work Orders</option>
                    {workOrders
                      .filter((w) => !selectedProject || String(w.project?._id || w.project) === String(selectedProject))
                      .map((w) => (
                        <option key={w._id} value={w._id}>
                          {w.woNo || w.woNumber}{w.label ? ` – ${w.label}` : ""}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
              </Row>

              {/* TABLE */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">Loading Contractor DCs...</p>
                </div>
              ) : deliveries.length === 0 ? (
                <div className="text-center py-5 border rounded bg-light">
                  <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
                  <h5>No Contractor Delivery Challans Found</h5>
                  <p className="text-muted">Create a new Contractor DC linked to a Contractual Work Order (CWO).</p>
                  <Link
                    to={`/contractor-delivery/add${selectedProject ? `?project=${selectedProject}` : ""}`}
                    className="btn btn-outline-primary btn-sm rounded-pill mt-2"
                  >
                    <i className="fas fa-plus me-1"></i> Create Contractor DC
                  </Link>
                </div>
              ) : (
                <div className="table-responsive border rounded bg-white">
                  <Table hover responsive className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th style={{ width: "4%" }} className="text-center">#</th>
                        <th style={{ width: "14%" }}>DC Number</th>
                        <th style={{ width: "16%" }}>Project & WO</th>
                        <th style={{ width: "12%" }}>CWO No.</th>
                        <th style={{ width: "16%" }}>Contractor</th>
                        <th style={{ width: "10%" }}>Delivery Date</th>
                        <th style={{ width: "8%" }} className="text-center">Items</th>
                        <th style={{ width: "10%" }}>Vehicle</th>
                        <th style={{ width: "8%" }} className="text-center">Status</th>
                        <th style={{ width: "8%" }} className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveries.map((d, index) => (
                        <tr key={d._id}>
                          <td className="text-center align-middle text-muted small">
                            {(currentPage - 1) * 15 + index + 1}
                          </td>
                          <td className="align-middle">
                            <span className="fw-bold text-primary">{d.deliveryNumber || d.challanNumber}</span>
                            {d.challanNumber && d.challanNumber !== d.deliveryNumber && (
                              <small className="d-block text-muted">Ref: {d.challanNumber}</small>
                            )}
                          </td>
                          <td className="align-middle">
                            <div className="fw-bold text-dark">{d.project?.projectName || "-"}</div>
                            <small className="text-muted">{d.workOrder?.woNo || d.workOrder?.woNumber || "-"}</small>
                          </td>
                          <td className="align-middle">
                            <span className="badge bg-light text-dark border">
                              {d.contractualWorkOrder?.cwoNo || "-"}
                            </span>
                          </td>
                          <td className="align-middle">
                            <div className="fw-bold text-dark">{d.contractor?.name || "-"}</div>
                            <small className="text-muted">{d.contractor?.phone || ""}</small>
                          </td>
                          <td className="align-middle text-muted small">
                            {formatDate(d.deliveryDate)}
                          </td>
                          <td className="text-center align-middle">
                            <Badge bg="secondary" pill>
                              {d.items?.length || 0}
                            </Badge>
                          </td>
                          <td className="align-middle text-muted small">
                            {d.dispatchDetails?.vehicleNumber || "-"}
                          </td>
                          <td className="text-center align-middle">
                            {getStatusBadge(d.status)}
                          </td>
                          <td className="text-center align-middle">
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                variant="light"
                                size="sm"
                                className="btn-icon rounded-circle border-0"
                              >
                                <i className="fas fa-ellipsis-v"></i>
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end" as={PortalMenu}>
                                {(d.status === "Pending" || d.status === "Draft") && (
                                  <>
                                    <Dropdown.Item
                                      className="text-success"
                                      onClick={() => handleAccept(d._id)}
                                      disabled={actionLoading === d._id}
                                    >
                                      <i className="fas fa-check-circle me-2"></i> Accept
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      className="text-danger"
                                      onClick={() => handleReject(d._id)}
                                      disabled={actionLoading === d._id}
                                    >
                                      <i className="fas fa-times-circle me-2"></i> Reject
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                  </>
                                )}
                                <Dropdown.Item
                                  onClick={() => handlePreviewPdf(d._id)}
                                  disabled={previewingPdf === d._id}
                                >
                                  <i className="fas fa-eye text-info me-2"></i> {previewingPdf === d._id ? "Opening..." : "Preview PDF"}
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleDownloadPdf(d._id, d.deliveryNumber)}
                                >
                                  <i className="fas fa-file-pdf text-danger me-2"></i> Download PDF
                                </Dropdown.Item>
                                <Dropdown.Item
                                  as={Link}
                                  to={`/contractor-delivery/edit/${d._id}`}
                                >
                                  <i className="fas fa-edit text-primary me-2"></i> Edit
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  className="text-danger"
                                  onClick={() => handleDelete(d._id, d.deliveryNumber)}
                                >
                                  <i className="fas fa-trash-alt me-2"></i> Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* PAGINATION */}
              {totalCount > 15 && (
                <div className="d-flex justify-content-end mt-3">
                  <AppPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(p) => setCurrentPage(p)}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </div>

      <SignatureModal
        show={showApproveModal}
        onHide={() => {
          setShowApproveModal(false);
          setApprovingId(null);
        }}
        onSave={handleSaveSignature}
        title="Accept Contractor Delivery Challan"
      />
    </Fragment>
  );
};

export default ContractorDeliveryList;

